import { CantileverButton } from 'components/CantileverButton'
import {
  DocumentMetadataUpdate,
  useKnowledgeDocumentDataContext,
} from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentDataContext'
import { useKnowledgeDocumentUXContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import { useGeckoPermissions } from 'features/permissions'
import {
  Check,
  ChevronDown,
  ChevronUp,
  FileText,
  Pencil,
  X,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { PERMISSION_CANTILEVER_DOCUMENT_OCR_ADMIN } from 'types'

const Container = styled.div`
  flex-shrink: 0;
  background-color: ${({ theme }) => theme.forecast.cardGroupBackground};
  border-top: 0.0625rem solid ${({ theme }) => theme.forecast.border};
`

const Header = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem;
  background: none;
  border: none;
  cursor: pointer;
  color: inherit;
  width: 100%;

  &:hover {
    background-color: ${({ theme }) => theme.forecast.pageBackground1};
  }
`

const HeaderTitle = styled.span`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.defaultText};
`

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`

const Content = styled.div<{ $isExpanded: boolean }>`
  display: ${({ $isExpanded }) => ($isExpanded ? 'grid' : 'none')};
  grid-template-columns: 1fr 1fr;
  gap: 0.375rem;
  padding: 0.5rem;
  border-top: 0.0625rem solid ${({ theme }) => theme.forecast.border};
`

const MetadataItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  padding: 0.375rem 0.5rem;
  background: rgb(38, 38, 38);
  border-radius: 0.25rem;
`

const Label = styled.div`
  font-size: 0.6875rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: ${({ theme }) => theme.forecast.tertiaryText};
`

const Value = styled.div`
  font-size: 0.8125rem;
  font-weight: 500;
  color: ${({ theme }) => theme.forecast.defaultText};
  word-break: break-word;
`

const EmptyValue = styled.span`
  color: ${({ theme }) => theme.forecast.tertiaryText};
  font-style: italic;
`

const EditInput = styled.input`
  width: 100%;
  padding: 0.25rem 0.375rem;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.forecast.defaultText};
  background: ${({ theme }) => theme.forecast.pageBackground1};
  border: 0.0625rem solid ${({ theme }) => theme.forecast.border};
  border-radius: 0.25rem;
  outline: none;

  &:focus {
    border-color: ${({ theme }) => theme.forecast.primary};
  }
`

type MetadataField = {
  key: keyof DocumentMetadataUpdate
  label: string
}

const METADATA_FIELDS: MetadataField[] = [
  { key: 'system', label: 'System' },
  { key: 'vendor_name', label: 'Vendor' },
  { key: 'document_type', label: 'Doc Type' },
  { key: 'drawing_number', label: 'Drawing #' },
  { key: 'revision_code', label: 'Revision' },
  { key: 'revision_date', label: 'Rev. Date' },
]

export function DocumentMetadataSection() {
  const { hasPermissions } = useGeckoPermissions()
  const { currentDocumentId } = useKnowledgeDocumentUXContext()
  const { systemDocumentEntries, updateDocumentMetadata } =
    useKnowledgeDocumentDataContext()

  const [isExpanded, setIsExpanded] = useState(true)
  const [isEditing, setIsEditing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [editedValues, setEditedValues] = useState<DocumentMetadataUpdate>({})

  const canEditMetadata = hasPermissions([
    PERMISSION_CANTILEVER_DOCUMENT_OCR_ADMIN,
  ])

  const selectedDocument = useMemo(
    () =>
      systemDocumentEntries.find(
        (entry) => entry.entry.id === currentDocumentId
      ),
    [systemDocumentEntries, currentDocumentId]
  )

  useEffect(() => {
    if (selectedDocument) {
      setEditedValues({
        system: selectedDocument.entry.data.system || '',
        vendor_name: selectedDocument.entry.data.vendor_name || '',
        document_type: selectedDocument.entry.data.document_type || '',
        drawing_number: selectedDocument.entry.data.drawing_number || '',
        revision_code: selectedDocument.entry.data.revision_code || '',
        revision_date: selectedDocument.entry.data.revision_date || '',
      })
    }
  }, [selectedDocument])

  const handleToggleExpand = useCallback(() => {
    if (!isEditing) setIsExpanded((prev) => !prev)
  }, [isEditing])

  const handleEditClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    setIsEditing(true)
    setIsExpanded(true)
  }, [])

  const handleCancelEdit = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      setIsEditing(false)
      if (selectedDocument) {
        setEditedValues({
          system: selectedDocument.entry.data.system || '',
          vendor_name: selectedDocument.entry.data.vendor_name || '',
          document_type: selectedDocument.entry.data.document_type || '',
          drawing_number: selectedDocument.entry.data.drawing_number || '',
          revision_code: selectedDocument.entry.data.revision_code || '',
          revision_date: selectedDocument.entry.data.revision_date || '',
        })
      }
    },
    [selectedDocument]
  )

  const handleSaveEdit = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation()
      if (!selectedDocument) return

      setIsSaving(true)
      try {
        await updateDocumentMetadata(selectedDocument.entry.id, editedValues)
        setIsEditing(false)
      } catch {
        // Error handled in context
      } finally {
        setIsSaving(false)
      }
    },
    [selectedDocument, editedValues, updateDocumentMetadata]
  )

  const handleInputChange = useCallback(
    (key: keyof DocumentMetadataUpdate, value: string) => {
      setEditedValues((prev) => ({ ...prev, [key]: value }))
    },
    []
  )

  if (!currentDocumentId || !selectedDocument) return null

  return (
    <Container>
      <Header onClick={handleToggleExpand}>
        <HeaderTitle>
          <FileText size={14} />
          Document Metadata
        </HeaderTitle>
        <HeaderActions>
          {canEditMetadata &&
            (isEditing ? (
              <>
                <CantileverButton
                  ghost
                  iconOnly
                  disabled={isSaving}
                  title='Cancel'
                  onClick={handleCancelEdit}>
                  <X size={14} />
                </CantileverButton>
                <CantileverButton
                  ghost
                  iconOnly
                  disabled={isSaving}
                  title='Save'
                  onClick={handleSaveEdit}>
                  <Check size={14} />
                </CantileverButton>
              </>
            ) : (
              <CantileverButton
                ghost
                iconOnly
                title='Edit Metadata'
                onClick={handleEditClick}>
                <Pencil size={14} />
              </CantileverButton>
            ))}
          {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </HeaderActions>
      </Header>
      <Content $isExpanded={isExpanded}>
        {METADATA_FIELDS.map((field) => {
          const rawValue =
            selectedDocument.entry.data[
              field.key as keyof typeof selectedDocument.entry.data
            ]
          const displayValue = typeof rawValue === 'string' ? rawValue : ''

          return (
            <MetadataItem key={field.key}>
              <Label>{field.label}</Label>
              {isEditing ? (
                <EditInput
                  type='text'
                  value={editedValues[field.key] || ''}
                  onChange={(e) => handleInputChange(field.key, e.target.value)}
                  disabled={isSaving}
                />
              ) : (
                <Value>
                  {displayValue || <EmptyValue>Not set</EmptyValue>}
                </Value>
              )}
            </MetadataItem>
          )
        })}
      </Content>
    </Container>
  )
}
